import React, { FC, useEffect, useState } from "react";

// Components
import { LoginStep } from "./Steps/LoginStep";

// Types
import { AppointmentInformationDto } from "@apiModels/AppointmentInformationDto.csharp";

// Helpers
import { BookingApi } from "../Api/BookingApi";
import { ConfirmationModal } from "../BookingLegacy/FormElements";
import Modal from "react-modal";
import { MyAppointmentModal } from "./MyAppointmentModal";
import { MyAppointmentParams } from "../../Pages/MyAppointment/MyAppointmentParams.csharp";
import {LabelsProvider} from "../BookingButton/LabelsContext/LabelsContext";

const api = new BookingApi();

export enum Steps {
  login,
  panel,
  exit,
}

export const MyAppointmentLegacy: FC<MyAppointmentParams> = ({
  stepOneParams,
  stepTwoParams,
  stepThreeParams,
  stepFourParams,
  bookingLabels,
  modalThemeColor,
}) => {
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.login);
  const [errors, setErrors] = useState<string[]>([]);
  const [viewState, setViewState] = useState("");
  const [cancellationRequestLoading, setCancellationRequestLoading] =
    useState(false);
  const [isSmsConfirmationModalOpen, setIsSmsConfirmationModalOpen] =
    useState(false);
  const [smsConfirmationErrors, setSmsConfirmationErrors] = useState<string[]>(
    []
  );
  const [smsConfirmationLoading, setSmsConfirmationLoading] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [appointmentInformation, setAppointmentInformation] =
    useState<AppointmentInformationDto>();

  const [showMyAppointmentModal, setShowMyAppointmentModal] = useState(false);

  useEffect(() => {
    Modal.setAppElement("body");
  }, []);

  const sendCancellationRequest = async (
    smsCode: string,
    personalNumber: string
  ) => {
    try {
      setCancellationRequestLoading(true);

      const response = await api.sendCancellationRequest({
        cancellationCode: smsCode,
        personalIdNumber: personalNumber,
      });

      setErrors(response.errors);

      if (response.errors.length == 0) {
        setViewState(response.viewState);
        setIsSmsConfirmationModalOpen(true);
      }
    } catch (err: any) {
      setErrors([err.message]);
    } finally {
      setCancellationRequestLoading(false);
    }
  };

  const sendSmsConfirmationCode = async (confirmationCode: number) => {
    try {
      setSmsConfirmationLoading(true);

      const response = await api.sendCancelConfirmationCode({
        confirmationCode: `${confirmationCode}`,
        viewState,
      });

      if (
        response.errors.length != 0 &&
        hasAppointmentInformationSet(response.appointmentInformation)
      ) {
        setSmsConfirmationErrors(response.errors);
      } else {
        setAppointmentInformation(response.appointmentInformation);
        setViewState(response.viewState);
        setCurrentStep(Steps.panel);
        setShowMyAppointmentModal(true);
        setIsSmsConfirmationModalOpen(false);
        setErrors(response.errors);
      }
    } catch (err: any) {
      setSmsConfirmationErrors([err.message]);
    } finally {
      setSmsConfirmationLoading(false);
    }
  };

  const hasAppointmentInformationSet = (info: AppointmentInformationDto) => {
    return (
      !info.specialization || !info.service || !info.doctor || !info.location
    );
  };

  const cancelAppointment = async () => {
    try {
      setCancellationLoading(true);

      const response = await api.sendCancellationConfirmation({
        viewState: viewState,
      });

      setErrors(response.errors);
      if (response.errors.length == 0) {
        setCurrentStep(Steps.exit);
        return;
      }
    } catch (err: any) {
      setErrors([err.message]);
    } finally {
      setCancellationLoading(false);
    }
  };

  const exit = () => {
    window.location.href = "/";
  };

  const closeSmsModal = () => {
    setIsSmsConfirmationModalOpen(false);
    setCancellationRequestLoading(false);
  };

  const [resetFormCallbacks, setResetFormCallbacks] = useState<(() => void)[]>(
    []
  );

  const addFormResetCallback = (callback: () => void) => {
    setResetFormCallbacks((prev) => [...prev, callback]);
  };
  const closeMyAppointmentModal = () => {
    setShowMyAppointmentModal(false);
    resetFormCallbacks.forEach((callback) => callback());
    setErrors([]);
    setSmsConfirmationErrors([]);
  };

  return (
    <div className="body-text-container mb-5">
      <LabelsProvider bookingLabels={bookingLabels}>
        <LoginStep
          {...stepOneParams}
          sendCancellationRequest={sendCancellationRequest}
          isDataLoading={cancellationRequestLoading}
          apiErrors={errors}
          addResetCallback={addFormResetCallback}
        />
        {showMyAppointmentModal && (
          <MyAppointmentModal
            currentStep={currentStep}
            onClose={closeMyAppointmentModal}
            appointmentInformation={appointmentInformation!}
            errors={errors}
            cancellationLoading={cancellationLoading}
            cancelAppointment={cancelAppointment}
            goToHomePage={exit}
            stepFourParams={stepFourParams}
            stepThreeParams={stepThreeParams}
            Modal={Modal}
            modalThemeColor={modalThemeColor}
          />
        )}

        <ConfirmationModal
          onClose={closeSmsModal}
          onSubmit={sendSmsConfirmationCode}
          isOpen={isSmsConfirmationModalOpen}
          Modal={Modal}
          loading={smsConfirmationLoading}
          confirmationError={smsConfirmationErrors.join(" ")}
          content={stepTwoParams.content}
          heading={stepTwoParams.title}
          inputLabel={stepTwoParams.smsCodeLabel}
          buttonLabel={stepTwoParams.submitButtonText}
          addResetCallback={addFormResetCallback}
        />
      </LabelsProvider>
    </div>
  );
};
